import React from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';

import BaseComponent from '../../baseClasses/BaseComponent';
import Caret from '../../assets/images/caret.svg';
import PopupMenu from '../../components/PopupMenu';
import NotificationsPopup from '../../components/NotificationsPopup';
import SwitchCompany from '../../components/SwitchCompany';
import VendorLogo from '../../assets/images/ao-logo.svg';
import NotificationsBadgeIcon from '../../assets/images/notification-badge.svg';
import Iconoclast from '../Iconoclast';
import ModalBannerConstants from '../../constants/ModalBannerConstants';

import './Header.scss';
import dataModel from '../../models/DataModel';
import SessionManager from '../../helpers/session/SessionManager';
import AnalyticsLogger from '../../helpers/analytics/analyticsLogger';
import { userHasInsightsOnlyRole, INSIGHTS_DEFAULT_ROUTE } from '../../helpers/user/insightsRoleAccess';
import { getUserDisplayLabel, getUserInitials } from '../../helpers/user/userDisplay';
import CollectionDataSource from '../../dao/DataSource/CollectionDataSource';
import DataSource from '../../dao/DataSource/DataSource';

export class Header extends BaseComponent {

  constructor(props) {
    super(props);

    // Initialize all expected state variable here
    this.state = {
      showCompanies: false,
      showCompaniesKey: 1,
      displayNotifications: false,
      hideNotifications: false,
      user: dataModel.usersDAO.currentUser,
    };
    const currentRole = dataModel.usersDAO.currentUser ? dataModel.usersDAO.currentUser.role : null;
    this.analyticsLogger = new AnalyticsLogger({
      currentRole,
      isMobile: !!window.cordova,
    });
    this.closeSettings = this.closeSettings.bind(this);
  }
  
  handleCompaniesUpdate(companies, action) {
    if (action == 'prefetch') {
    } else {
      let filteredCompanies = [];
      if (companies) {
        filteredCompanies = companies.companies;
      }
      this.setState({ filteredCompanies: filteredCompanies, loadingCompanies: false });
    }
  }

  handleUserRetrieval(user, action, acData, fetchedData) {
    if (user && user.id == this.props.currentUser.id) {
      this.setState({ user });
    }
  }

  componentDidMount() {
    this.fetchData();
  }

  componentWillUnmount() {
    if (this.companiesCollectionDataSource) {
      this.companiesCollectionDataSource.release();
      this.companiesCollectionDataSource = null;
    }
    if (this.userDataSource) {
      this.userDataSource.release();
      this.userDataSource = null;
    }
  }

  componentDidUpdate(prevProps) {
    this.fetchData();
  }

  fetchData() {
    const { currentUser, currentCompany } = this.props;
    if (currentCompany && !this.companiesCollectionDataSource) {
      this.companiesCollectionDataSource = new CollectionDataSource('CompaniesCollection', 'Company', { companies: {} },
        this.handleCompaniesUpdate, 'headerCompanies', null, null, null,
        this.props.currentCompany.id, 'companies_header');
        // perPage, searchString, userId, companyId, subKey, filter, recordDescriptor
    }
    if (currentUser && !this.userDataSource) {
      this.userDataSource = new DataSource('User', this.props.currentUser.id, { user: { additionalFields: ['profile_image'], companies: {} } }, this.handleUserRetrieval);
      this.userDataSource.get();
    }
  }

  handleCloseMenu() {
    if (this.props.history.location.pathname != '/login') {
      //this setState would be a no-op if a logout is in progress
      this.setState({ menu: null, reportsMenu: null });
    }
  }

  handleSwitchCompanyCancel() {
    this.setState({
      showCompanies: false, menu: null,
    });
  }

  closeSettings() {
    dataModel.hideGlobalModal();
  }

  handleUserMenuSelection(type) {
    switch (type) {
    case 'logoff':
      this.props.dataModel.hideGlobalBanner();
      SessionManager.logoff();
      // Leave the authenticated route BEFORE wiping the store. onLogout() dispatches USER_LOGOUT,
      // which resets every slice (rootReducer in models/index.js), so new_users.permissions and
      // new_users.currentUser go undefined. This handler is invoked from PopupMenu's animation
      // setTimeout, i.e. outside a React event handler, so react-redux flushes the resulting
      // re-render synchronously: wiping first re-renders the still-mounted privileged screen with
      // no permissions and it throws (AFB: "Cannot read properties of undefined (reading
      // 'canCreateTask')" on the project template tasks screen) before we ever reach the push.
      this.props.history.push({ pathname: '/login' });
      this.props.onLogout();
      break;
    case 'switch-company':
      this.setState({ showCompanies: true, loadingCompanies: true, showCompaniesKey: this.state.showCompaniesKey + 1 });
      this.companiesCollectionDataSource.current();
      break;
    case 'settings':
      this.createSettings();
      break;
    default:
      break;
    }
  }

  /**
   * Insights-only users share this Header, so any gate that needs the role should resolve it here
   * rather than re-deriving it. Mirrors render()'s precedence: the freshly fetched user in state
   * wins over the prop. Note the switch-company and profile gates below still key off company
   * count / permissions only, so they can surface for an insights user who is super_admin or is in
   * more than one company — both of those destinations 403 or redirect. Not fixed here.
   */
  isInsightsOnlyUser() {
    const { permissions } = this.props;
    const currentUser = this.state.user ? this.state.user : this.props.currentUser;
    return userHasInsightsOnlyRole(currentUser) || !!(permissions && permissions.isInsightsOnlyUser);
  }

  handleUserDropdownClick(e) {
    if (!this.state.menu) {
      if (e) {
        e.stopPropagation();
      }
      const menuOptions = [];
      const { currentUser, permissions } = this.props;

      if (currentUser && (currentUser.super_admin || (currentUser.companies.length + currentUser.vendor_companies.length) > 1)) {
        menuOptions.push({ type: 'switch-company', name: this.textMap.switchCompany, dismiss: false });
      }

      if (permissions && (permissions.canEditMyUser || permissions.canEditUser || permissions.canViewUser)) {
        menuOptions.push({ type: 'profile', name: this.textMap.myProfile, link: '/my-profile', onClick: this.handleClick });
      }

      if (!this.isInsightsOnlyUser()) {
        menuOptions.push({ type: 'settings', name: `${this.textMap.settings}` });
      }

      menuOptions.push({ type: 'logoff', name: this.textMap.logoff });

      const menu = <PopupMenu right={10} top={30} width={200} menuOptions={menuOptions} size="small" pointerPosition={'top-right'}
        menuId="userMenu" handleClickAway={this.handleCloseMenu} handleMenuSelection={this.handleUserMenuSelection} />;
      this.setState({ menu: menu, reportMenu: null });
    }
  }

  createSettings() {
    const { currentUser } = this.props;
    const { user } = this.state;
    dataModel.showGlobalModal(ModalBannerConstants.USER_SETTINGS_MODAL, {
      height: 184,
      currentUser: user ? user : currentUser,
      isMobile: dataModel.mobileBridge.isMobile,
      closeSettings: this.closeSettings,
    });

  }

  handleKeyDown(e) {
    if (e.keyCode == 13 || e.keyCode == 40) {
      this.handleUserDropdownClick();
    }
  }

  handleNotificationsToggle() {
    if (!this.state.displayNotifications) {
      this.setState({ displayNotifications: !this.state.displayNotifications });
      this.analyticsLogger.logNotifications('NotificationsBell');
    }
  }

  handleNotificationsCallback() {
    this.setState({ displayNotifications: false });
  }

  handleShowAllNotifications() {
    return this.props.notifications;
  }

  handleSortOutNotActedOnNotifications() {
    let { notifications } = this.props;
    let notActedOnNotifications = notifications ? notifications.filter(notification => {
      if (!notification.action_at) {
        return notification;
      }
      else {
        return false;
      }
    }) : [];
    return notActedOnNotifications;
  }

  handleSortOutUnReadNotifications() {
    let { notifications } = this.props;
    let notReadNotifications = notifications ? notifications.filter(notification => {
      if (!notification.read_at) {
        return notification;
      }
      else {
        return false;
      }
    }) : [];
    return notReadNotifications;
  }

  handleCreateReportsMenu(e) {
    let { currentUser, permissions } = this.props;
    const { user } = this.state;
    if (user) {
      currentUser = user;
    }
    if (currentUser.role_name == 'editor' || currentUser.role_name == 'admin' || permissions.isSuperUser) {
      if (!this.state.reportsMenu) {
        if (e) {
          e.stopPropagation();
        }
        const menuOptions = [];

        menuOptions.push({ type: 'project', name: 'Project Reporting', link: '/reports/project' });
        menuOptions.push({ type: 'user', name: 'User Reporting', link: '/reports/user' });
        if (currentUser.role_name == 'admin' || permissions.isSuperUser) {
          menuOptions.push({ type: 'log', name: 'Activity Log', link: '/reports/activity-log' });
        }

        this.setState({ menu: null, reportsMenu: menuOptions });
      }
      else {
        this.setState({ reportsMenu: null });
      }
    }
    else {
      this.props.history.push('/reports/project/');
    }
  }
  
  getPorHref() {
    const pathname = this.props.location ? this.props.location.pathname : '';
    const pathArray = pathname.split('/').filter(Boolean);
    // Project-first POR (same pattern as CloseoutNotificationBanner), not /por/location/:id/project/:id
    if (pathArray[0] === 'projects' && pathArray[1] && /^\d+$/.test(pathArray[1])) {
      return '/por/project/' + pathArray[1] + '/location/all?tab=documents&navigation=skip_select';
    }
    if (pathArray[0] === 'tasks' && pathArray[1] === 'project' && pathArray[2] && /^\d+$/.test(pathArray[2])) {
      return '/por/project/' + pathArray[2] + '/location/all?tab=documents&navigation=skip_select';
    }
    return '/por/location';
  }

  handleClick(event,href) {
    const message = this.textMap.queryDataChanged;
    const { location } = this.props;
    const params = new URLSearchParams(location.search);
    const isProjectNew = params.get('isProjectNew');
    const pathArray = location.pathname.split('/');
    let projectId = null;
    if(pathArray && pathArray[1] == 'projects'){
      projectId = pathArray[2];
    }
    
    if(dataModel.requiredDataNotAvailable || isProjectNew)
    {
      event.preventDefault();
      dataModel.showGlobalModal('SYSTEM_POPUP', {
        message: 'If you leave this page, the new project you are creating will be deleted. If you wish to continue, click Yes. If you do not wish to continue, click No and provide the required fields on the page to save the project.',
        cancelButtonText: this.textMap.no.toUpperCase(),
        okButtonText: this.textMap.yes.toUpperCase(),
        width: 420,
        height: 200,
        handleOwnerOk: () => {
          this.deleteNewProject(href, projectId);
        },
      });
    }
    else if(dataModel.dataChanged){
      event.preventDefault();
      dataModel.showGlobalModal('SYSTEM_POPUP', {
        message: message,
        cancelButtonText: this.textMap.no.toUpperCase(),
        okButtonText: this.textMap.yes.toUpperCase(),
        width: 300,
        height: 180,
        handleOwnerOk: () => {
          dataModel.dataChanged = false;
          this.props.history.push(href);
        },
      });
    }
    else{
      this.props.history.push(href);
    }
  }

  deleteNewProject(href, projectId){
    dataModel.projectsDAO.deleteProject(projectId).then(response => {
      this.props.history.push(href);
      dataModel.requiredDataNotAvailable = false;
      dataModel.dataChanged = false;
    });
  }

  render() {
    let notificationCount = 0;
    let { currentUser, company, notifications, selectedPage, permissions } = this.props;
    const { user, displayNotifications } = this.state;
    if (!currentUser) {
      return null;
    }

    if (user) {
      currentUser = user;
    }

    const useInsightsMinimalHeader = this.isInsightsOnlyUser();

    if (!useInsightsMinimalHeader && !permissions) {
      return null;
    }

    if (useInsightsMinimalHeader) {
      const userNameInsights = getUserDisplayLabel(currentUser);
      const profileImageInsights = currentUser && currentUser.profile_image;
      let userImageInsights = null;
      if (profileImageInsights) {
        userImageInsights = <img className="profile-image" alt="User" src={profileImageInsights} />;
      }
      else if (currentUser) {
        const initialsInsights = getUserInitials(currentUser);
        userImageInsights = (
          <div className="default-user-image-container">
            <div className="default-user-image" />
            <div className="user-initials">{initialsInsights}</div>
          </div>
        );
      }
      let accent_colorInsights = company ? company.accent_color : '#CD030A';
      let logoInsights = company ? company.logo : null;
      if (!logoInsights) {
        logoInsights = VendorLogo;
      }
      const borderStyleInsights = { borderColor: accent_colorInsights };
      const userMenuInsights = this.state.menu ? this.state.menu : null;
      let showCompaniesInsights = null;
      if (this.state.showCompanies) {
        showCompaniesInsights = <SwitchCompany height={32} key={this.state.showCompaniesKey} width={219} top={63} left={60}
          displayFontStyle={{ fontSize: '12px', fontFamily: 'Open Sans', color: '#000000', lineHeight: '32px' }}
          handleCancel={this.handleSwitchCompanyCancel} companies={this.state.filteredCompanies} loadingCompanies={this.state.loadingCompanies}
        />;
      }
      return (
        <div className={'header' + (this.props.isBulkAssign ? ' header--bulk-assign' : '')} >
          <div className="row1">
            <Link to={INSIGHTS_DEFAULT_ROUTE} onClick={(e) => this.handleClick(e, INSIGHTS_DEFAULT_ROUTE)}><img alt="Logo" className="vendor-logo" src={logoInsights} /></Link>
            <div className="company-name">{company ? company.name : ''}</div>
            <div className="header-right-side">
              {showCompaniesInsights}
              <div className="user-dropdown" onMouseDown={this.handleUserDropdownClick} onKeyDown={this.handleKeyDown} tabIndex={1}>
                {userMenuInsights}
                <div className="user-line">
                  {userImageInsights}
                  <p className="user-name">{userNameInsights}</p>
                  <img alt="Caret" className="caret" src={Caret} />
                </div>
              </div>
            </div>
          </div>
          <div className="divider">
            <hr style={borderStyleInsights}/>
          </div>
        </div>
      );
    }

    let showCompanies = null;

    if (this.state.showCompanies) {
      showCompanies = <SwitchCompany height={32} key={this.state.showCompaniesKey} width={219} top={63} left={60}
        displayFontStyle={{ fontSize: '12px', fontFamily: 'Open Sans', color: '#000000', lineHeight: '32px' }}
        handleCancel={this.handleSwitchCompanyCancel} companies={this.state.filteredCompanies} loadingCompanies={this.state.loadingCompanies}
      />;
    }

    const userName = getUserDisplayLabel(currentUser);
    let profileImage = currentUser && currentUser.profile_image;
    let userImage = null;
    if (profileImage) {
      userImage = <img className="profile-image" alt="User" src={profileImage} />;
    }
    else if (currentUser) {
      const initials = getUserInitials(currentUser);
      userImage = (
        <div className="default-user-image-container">
          <div className="default-user-image" />
          <div className="user-initials">{initials}</div>
        </div>
      );
    }

    const unreadNotifications = this.handleSortOutUnReadNotifications(notifications);
    notificationCount = unreadNotifications ? unreadNotifications.length : 0;

    let accent_color = company ? company.accent_color : '#CD030A';
    let logo = company ? company.logo : null;
    if (!logo) {
      logo = VendorLogo;
    }

    const borderStyle = { borderColor: accent_color };
    const userMenu = this.state.menu ? this.state.menu : null;

    let notificationsPopup = null;
    if (displayNotifications) {
      notificationsPopup = <NotificationsPopup right={135} top={67} handleUpdateNotification={this.handleUpdateNotification}
        closeCallback={this.handleNotificationsCallback} />;
    }

    let companiesButton = null;
    if (this.props.permissions && (this.props.permissions.isSuperUser || this.props.permissions.canEditCompany ||
      this.props.permissions.canViewCompany)) {
      companiesButton = (
        <Link className="header-option" to="/companies" onClick={(e) => this.handleClick(e, '/companies')}>
          <Iconoclast type='companies' selected={selectedPage == 'companies'} />
          <div className='header-label companies'>{this.textMap.companies.toUpperCase()}</div>
        </Link>
      );
    }

    let projectTypesButton = null;
    if (this.props.permissions
      && (this.props.permissions.isSuperUser || this.props.permissions.canCreateCompany)) {
      projectTypesButton = (
        <Link className="header-option project-types" to="/project-types" onClick={(e) => this.handleClick(e, '/project-types')}>
          <Iconoclast type='project-types' selected={selectedPage == 'project-types'} />
          <div className='header-label'>{this.textMap.projectTypeAndTemplates.toUpperCase()}</div>
        </Link>
      );
    }

    let projectsButton = null;
    if (this.props.permissions && (this.props.permissions.canEditProject ||
      this.props.permissions.canViewProject)) {
      projectsButton = (
        <Link className="header-option" to="/projects/summary" onClick={(e) => this.handleClick(e, '/projects/summary')}>
          <Iconoclast type='projects' selected={selectedPage == 'projects'}/>
          <div className='header-label projects'>{this.textMap.projects.toUpperCase()}</div>
        </Link>
      );
    }

    let dashboardButton = null;
    let canViewDashboard = false;
    if (this.props.currentUser.role.name == 'admin' ||
      this.props.currentUser.role.name == 'editor' ||
      this.props.currentUser.super_admin) {
      canViewDashboard = true;
    }
    else if (this.props.permissions && this.props.permissions.canViewProject &&
      this.props.currentCompany && this.props.currentUser.companies[0].id ==
      this.props.currentCompany.id) {
      canViewDashboard = true;
    }
    if (canViewDashboard) {
      dashboardButton = (
        <Link className="header-option" to={INSIGHTS_DEFAULT_ROUTE} onClick={(e) => this.handleClick(e, INSIGHTS_DEFAULT_ROUTE)}>
          <Iconoclast type='dashboard' selected={selectedPage == 'manager-dashboard'} />
          <div className='header-label dashboard'>DASHBOARD</div>
        </Link>
      );
    }

    let referenceDocumentLibraryButton = null;
    //TODO: would be nice to have functions for long boolean expressions such as this
    //if user can view project, the current company is not the admin company, and user is an admin of the current company, or user is a super user
    if (this.props.currentCompany && this.props.currentUser && this.props.permissions &&
      ((this.props.permissions.isSuperUser || (this.props.currentUser.companies[0].id ==
      this.props.currentCompany.id && this.props.currentUser.role_name == 'admin')))) {
      referenceDocumentLibraryButton = (
          <Link className="header-option" to="/reference-documents" onClick={(e) => this.handleClick(e, '/reference-documents')}>
            <Iconoclast type='documents' selected={selectedPage == 'reference-documents'}/>
            <div className='header-label documents'>{this.textMap.documents.toUpperCase()}</div>
          </Link>
        );
    }

    let notificationBadge = null;
    if (notificationCount > 0) {
      notificationBadge = <img className="notifications-badge" src={NotificationsBadgeIcon} alt="unread notifications"/>;
    }

    return (
      // Replace with the appropriate HTML
      <div className={'header' + (this.props.isBulkAssign ? ' header--bulk-assign' : '')} >
        {notificationsPopup}
        <div className="row1">
          <Link to="/projects/summary" onClick={(e) => this.handleClick(e, '/projects/summary')}><img alt="Logo" className="vendor-logo" src={logo} /></Link>
          <div className="company-name">{company ? company.name : ''}</div>
          <div className="header-right-side">
            {showCompanies}
            <div className="user-dropdown" onMouseDown={this.handleUserDropdownClick} onKeyDown={this.handleKeyDown} tabIndex={1}>
              {userMenu}
              <div className="user-line">
                {userImage}
                <p className="user-name">{userName}</p>
                <img alt="Caret" className="caret" src={Caret} />
              </div>
            </div>

            <div className="header-option" onClick={this.handleNotificationsToggle}>
              <Iconoclast type='notifications' selected={displayNotifications} />
              <div className='header-label notifications'>{this.textMap.notifications.toUpperCase()}</div>
              {notificationBadge}
            </div>

            {referenceDocumentLibraryButton}

            <Link className="header-option" to="/directory" onClick={(e) => this.handleClick(e, '/directory')}>
              <Iconoclast type='directory' selected={selectedPage == 'directory'}/>
              <div className='header-label directories'>{this.textMap.directories.toUpperCase()}</div>
            </Link>

            <Link className="header-option" to="/tasks/project/all" onClick={(e) => this.handleClick(e, '/tasks/project/all')}>
              <Iconoclast type='tasks' selected={selectedPage == 'tasks'} />
              <div className='header-label tasks'>{this.textMap.tasks.toUpperCase()}</div>
            </Link>

            {projectsButton}

            <Link className="header-option por" to={this.getPorHref()} onClick={(e) => this.handleClick(e, this.getPorHref())}>
              <Iconoclast type='locations' selected={selectedPage == 'por'}/>
              <div className='header-label locations' id="plan-of-record">{this.textMap.planOfRecord.toUpperCase()}</div>
            </Link>

            {projectTypesButton}
            {companiesButton}

            {dashboardButton}
    
          </div>
        </div>
        <div className="divider">
          <hr style={borderStyle}/>
        </div>
       </div>
    );
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    onLogout: () => {
      dispatch({ type: 'USER_LOGOUT' });
    },
  };
};
const mapStateToProps = (state, ownProps) => {
  let company = state.new_companies.currentCompany;
  if (company && state.new_companies[company.id]) {
    company = state.new_companies[company.id];
  }
  return {
    dataModel: dataModel,
    currentUser: state.new_users.currentUser,
    permissions: state.new_users.permissions,
    notifications: state.notifications.notifications,
    notificationsDataModel: dataModel.notificationsDataModel,
    currentCompany: state.new_companies.currentCompany,
    company: company,
    results: state.main.results,
  };
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Header));



// WEBPACK FOOTER //
// ./src/components/Header/index.js